import { useNotification } from '../../shared/notification/useNotification';
import { useTranslation } from 'react-i18next';
import { ForwardingType } from '../models/Forwarding/Forwarding';
import { BehApiRoutes, behClient } from '../beh-api';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { TFunction } from 'i18next';
import { ReasonsType, ReasonType } from '../../shared/utils/Error/ErrorCode';

export interface DestinationConfig {
  name: string;
  forwardingType: ForwardingType;
  publicServiceIdentifier: string;
  forwardingTargetConfig: {
    destinationId: string;
    publicServiceIdentifier: string;
  };
}

interface CreateDestinationConfig {
  orgUnitId: string;
  destinationConfig: DestinationConfig;
}

export const ForwardingProblemType = {
  selfForwardingIsNotAllowed:
    'behoerdenclient/problems/illegalForwardingConfig/selfForwardingIsNotAllowed',
  forwardingTargetDestinationNotFound:
    'behoerdenclient/problems/illegalForwardingConfig/forwardingTargetDestinationNotFound',
  forwardingTargetPublicServiceNotFound:
    'behoerdenclient/problems/illegalForwardingConfig/forwardingTargetPublicServiceNotFound',
  fitconnectTargetDestinationIsNotActive:
    'behoerdenclient/problems/illegalForwardingConfig/fitconnectTargetDestinationIsNotActive',
  fitconnectTargetDoesNotSupportSchema:
    'behoerdenclient/problems/illegalForwardingConfig/fitconnectTargetDoesNotSupportSchema',
  fitconnectSenderCredentialsMissing:
    'behoerdenclient/problems/illegalForwardingConfig/fitconnectSenderCredentialsMissing',
  fitconnectSourceDestinationWasNotFound:
    'behoerdenclient/problems/illegalForwardingConfig/fitconnectSourceDestinationWasNotFound',
  clientInvalid: 'behoerdenclient/problems/clientInvalid',
  clientInvalidNotASenderCredential:
    'behoerdenclient/problems/clientInvalid/notASenderCredential',
  forwardingConflict: 'behoerdenclient/problems/forwardingConflict'
} as const;

export const forwardingProblemTypes: ReadonlySet<string> = new Set(
  Object.values(ForwardingProblemType)
);

export interface ForwardingErrorDetails {
  message: string;
  schemaUris: Array<string>;
}

export function resolveForwardingErrorMessage(
  t: TFunction,
  type: string | undefined,
  reasons?: Array<ReasonsType>
): ForwardingErrorDetails | undefined {
  const withDestinationId = (
    genericKey: string,
    detailsKey: string,
    reasonTypes: Array<ReasonType>
  ): ForwardingErrorDetails => {
    const destinationId = reasons?.find((reason) =>
      reasonTypes.includes(reason.type)
    )?.destinationId;
    return {
      message: destinationId ? t(detailsKey, { destinationId }) : t(genericKey),
      schemaUris: []
    };
  };

  switch (type) {
    case ForwardingProblemType.selfForwardingIsNotAllowed:
      return {
        message: t('api.error.forwardingConfig.selfForwardingIsNotAllowed'),
        schemaUris: []
      };
    case ForwardingProblemType.forwardingTargetDestinationNotFound:
      return withDestinationId(
        'api.error.forwardingConfig.forwardingTargetDestinationNotFound',
        'api.error.forwardingConfig.forwardingTargetDestinationNotFound.details',
        [ReasonType.BEHOERDENCLIENT_REASONS_NOT_FOUND_DESTINATION_ID]
      );
    case ForwardingProblemType.forwardingTargetPublicServiceNotFound: {
      const publicServiceIdentifier = reasons?.find(
        (reason) =>
          reason.type ===
          ReasonType.BEHOERDENCLIENT_REASONS_NOT_FOUND_PUBLIC_SERVICE_IDENTIFIER
      )?.publicServiceIdentifier;
      return {
        message: publicServiceIdentifier
          ? t(
              'api.error.forwardingConfig.forwardingTargetPublicServiceNotFound.details',
              { publicServiceIdentifier }
            )
          : t(
              'api.error.forwardingConfig.forwardingTargetPublicServiceNotFound'
            ),
        schemaUris: []
      };
    }
    case ForwardingProblemType.fitconnectTargetDestinationIsNotActive:
      return withDestinationId(
        'api.error.forwardingConfig.fitConnectTargetDestinationIsNotActive',
        'api.error.forwardingConfig.fitConnectTargetDestinationIsNotActive.details',
        [ReasonType.BEHOERDENCLIENT_REASONS_INACTIVE_DESTINATION_ID]
      );
    case ForwardingProblemType.fitconnectTargetDoesNotSupportSchema:
      return {
        message: t(
          'api.error.forwardingConfig.fitConnectTargetDoesNotSupportSchema'
        ),
        schemaUris: [
          ...(reasons?.find(
            (reason) =>
              reason.type ===
              ReasonType.BEHOERDENCLIENT_REASONS_UNSUPPORTED_SCHEMA_URIS
          )?.schemaUris ?? [])
        ].sort()
      };
    case ForwardingProblemType.fitconnectSenderCredentialsMissing:
      return {
        message: t(
          'api.error.forwardingConfig.fitconnectSenderCredentialsMissing'
        ),
        schemaUris: []
      };
    case ForwardingProblemType.fitconnectSourceDestinationWasNotFound:
      return withDestinationId(
        'api.error.forwardingConfig.fitconnectSourceDestinationNotFound',
        'api.error.forwardingConfig.fitconnectSourceDestinationNotFound.details',
        [ReasonType.BEHOERDENCLIENT_REASONS_NOT_FOUND_SOURCE_DESTINATION_ID]
      );
    case ForwardingProblemType.clientInvalid:
    case ForwardingProblemType.clientInvalidNotASenderCredential:
      return {
        message: t(
          'api.error.forwardingConfig.fitconnectSenderCredentialsInvalid'
        ),
        schemaUris: []
      };
    case ForwardingProblemType.forwardingConflict:
      return {
        message: t('api.error.forwardingConfig.forwardingConflict'),
        schemaUris: []
      };
    default:
      return undefined;
  }
}

function forwardingErrorIdHandlers(
  t: TFunction,
  notifyUser: ReturnType<typeof useNotification>['notifyUser'],
  suppressedErrorTypes: ReadonlySet<string> = new Set()
): Record<string, (reasons?: Array<ReasonsType>) => void> {
  const handlers: Record<string, (reasons?: Array<ReasonsType>) => void> = {};
  forwardingProblemTypes.forEach((errorType) => {
    handlers[errorType] = suppressedErrorTypes.has(errorType)
      ? () => {}
      : (reasons) => {
          const error = resolveForwardingErrorMessage(t, errorType, reasons);
          if (error) {
            notifyUser({
              intent: 'error',
              title: error.message
            });
          }
        };
  });
  return handlers;
}

function useInvalidateForwardings() {
  const queryClient = useQueryClient();

  return (variables: CreateDestinationConfig) => {
    queryClient.invalidateQueries({
      queryKey: [
        BehApiRoutes.fowardingsByPublicServicesByIdAndByOrgUnit(
          variables.orgUnitId,
          variables.destinationConfig.publicServiceIdentifier
        )
      ]
    });
  };
}

export interface ForwardingHookOptions {
  /** Fehler-Typen, deren Toast unterdrückt wird, damit der Aufrufer sie selbst behandelt. */
  suppressedErrorTypes?: ReadonlySet<string>;
}

export function useOrganizationForwardingPost(options?: ForwardingHookOptions) {
  const { notifyUserFromRequest, notifyUser } = useNotification();
  const { t } = useTranslation();
  const invalidateForwardings = useInvalidateForwardings();

  return useMutation({
    mutationFn: ({ destinationConfig, orgUnitId }: CreateDestinationConfig) =>
      notifyUserFromRequest(
        () =>
          behClient.post(
            BehApiRoutes.forwardingsByOrgUnit(orgUnitId),
            destinationConfig
          ),
        {
          errorMessage: t('api.error.createFitConnectSenderCreds'),
          successMessage: t('api.success.createFitConnectSenderCreds'),
          errorIdHandler: forwardingErrorIdHandlers(
            t,
            notifyUser,
            options?.suppressedErrorTypes
          )
        }
      ),
    onSuccess: (_, variables) => {
      invalidateForwardings(variables);
    }
  });
}

export function useOrganizationForwardingPut(options?: ForwardingHookOptions) {
  const { notifyUserFromRequest, notifyUser } = useNotification();
  const { t } = useTranslation();
  const invalidateForwardings = useInvalidateForwardings();

  return useMutation({
    mutationFn: ({ destinationConfig, orgUnitId }: CreateDestinationConfig) =>
      notifyUserFromRequest(
        () =>
          behClient.put(
            BehApiRoutes.forwardingsByOrgUnit(orgUnitId),
            destinationConfig
          ),
        {
          errorMessage: t('api.error.createFitConnectSenderCreds'),
          successMessage: t('api.success.createFitConnectSenderCreds'),
          errorIdHandler: forwardingErrorIdHandlers(
            t,
            notifyUser,
            options?.suppressedErrorTypes
          )
        }
      ),
    onSuccess: (_, variables) => {
      invalidateForwardings(variables);
    }
  });
}

export function useOrganizationForwardingCheck(
  options?: ForwardingHookOptions
) {
  const { notifyUserFromRequest, notifyUser } = useNotification();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ destinationConfig, orgUnitId }: CreateDestinationConfig) =>
      notifyUserFromRequest(
        () =>
          behClient.post(
            BehApiRoutes.forwardingsCheckByOrgUnit(orgUnitId),
            destinationConfig
          ),
        {
          loadingMessage: t('api.loading.createFitConnectSenderCreds'),
          errorMessage: t('api.error.createFitConnectSenderCreds'),
          successMessage: t('api.success.checkForwardingTarget'),
          errorIdHandler: forwardingErrorIdHandlers(
            t,
            notifyUser,
            options?.suppressedErrorTypes
          )
        }
      )
  });
}

interface DeleteDestinationConfig {
  orgUnitId: string;
  forwardingType: string;
  destinationId: string;
  publicServiceIdentifier: string;
  fromPublicServiceIdentifier: string;
}

export function useOrganizationForwardingDelete() {
  const { notifyUserFromRequest } = useNotification();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({
      orgUnitId,
      forwardingType,
      publicServiceIdentifier,
      fromPublicServiceIdentifier,
      destinationId
    }: DeleteDestinationConfig) =>
      notifyUserFromRequest(
        () =>
          behClient.delete(BehApiRoutes.forwardingsByOrgUnit(orgUnitId), {
            params: {
              'forwarding-type': forwardingType,
              'public-service-identifier': publicServiceIdentifier,
              'destination-id': destinationId,
              'from-public-service-identifier': fromPublicServiceIdentifier
            }
          }),
        {
          loadingMessage: t('api.loading.deleteDestination'),
          errorMessage: t('api.error.deleteDestination'),
          successMessage: t('api.success.deleteDestination')
        }
      )
  });
}
